import { useTranslation } from "react-i18next";
import { UploadCloud, X, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface BrandingChoiceProps {
  setWatermarkChoice: (choice: "yes" | "no") => void;
  prevStep: () => void;
  isMobile: boolean;
  templatesCount: number;
}

export const BrandingChoice = ({ setWatermarkChoice, prevStep, isMobile, templatesCount }: BrandingChoiceProps) => {
  const { t } = useTranslation();
  const showMobileWarning = isMobile && templatesCount === 0;

  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h2 className="font-h3 text-xl 2k:text-4xl 4k:text-6xl">
          {t("createVideo.watermarkQuestion") ||
            "Do you want to add branding to your video?"}
        </h2>
        <p className="text-muted-foreground 2k:text-2xl 4k:text-4xl">
          {t("createVideo.watermarkQuestionHint") ||
            "Choose whether to overlay your brand logo on the video."}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <button
          type="button"
          disabled={showMobileWarning}
          onClick={() => setWatermarkChoice("yes")}
          className={cn(
            "group p-8 2k:p-14 4k:p-20 bg-white border border-black/5 rounded-3xl text-left transition-all",
            showMobileWarning 
              ? "opacity-50 cursor-not-allowed" 
              : "hover:border-black hover:shadow-xl"
          )}
        >
          <div className={cn(
            "w-16 h-16 2k:size-24 4k:size-32 rounded-2xl flex items-center justify-center mb-6 transition-transform",
            showMobileWarning ? "bg-black/20" : "bg-black group-hover:scale-110"
          )}>
            <UploadCloud className="w-8 h-8 2k:size-12 4k:size-16 text-white" />
          </div>
          <h3 className="text-2xl 2k:text-4xl 4k:text-6xl font-bold mb-2">
            {t("createVideo.yesAddWatermark") || "Yes, add branding"}
          </h3>
          <p className="text-muted-foreground 2k:text-2xl 4k:text-4xl">
            {t("createVideo.yesAddWatermarkHint") ||
              "Upload your logo and choose its position."}
          </p>

          {showMobileWarning && (
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-2xl flex gap-3 items-start text-amber-800">
              <AlertTriangle className="size-5 shrink-0 mt-0.5 text-amber-600" />
              <div className="space-y-1">
                <p className="text-xs font-bold leading-tight">
                  {t("createVideo.mobileBrandingRequired") || "Branding Template Required"}
                </p>
                <p className="text-[10px] font-medium leading-normal">
                  {t("createVideo.mobileBrandingInstruction") ||
                    "To add branding, please use the desktop version of the app to create a branding template first. On mobile, you can only use existing saved templates."}
                </p>
              </div>
            </div>
          )}
        </button>

        <button
          type="button"
          onClick={() => setWatermarkChoice("no")}
          className="group p-8 2k:p-14 4k:p-20 bg-white border border-black/5 rounded-3xl hover:border-black hover:shadow-xl transition-all text-left"
        >
          <div className="w-16 h-16 2k:size-24 4k:size-32 rounded-2xl bg-black/5 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
            <X className="w-8 h-8 2k:size-12 4k:size-16 text-black" />
          </div>
          <h3 className="text-2xl 2k:text-4xl 4k:text-6xl font-bold mb-2">
            {t("createVideo.noSkipWatermark") || "No, skip branding"}
          </h3>
          <p className="text-muted-foreground 2k:text-2xl 4k:text-4xl">
            {t("createVideo.noSkipWatermarkHint") ||
              "Produce video without any watermark."}
          </p>
        </button>
      </div>

      <div className="hidden lg:flex justify-start pt-8">
        <Button
          type="button"
          variant="outline"
          onClick={prevStep}
          className="rounded-full border-black/10 hover:bg-white px-12 py-7 2k:px-20 2k:py-12 4k:px-30 4k:py-18 text-lg 2k:text-3xl 4k:text-5xl cursor-pointer"
        >
          {t("common.back") || "Back"}
        </Button>
      </div>
    </div>
  );
};
